import {
    Link,
    Menu,
    MenuItem,
    styled,
    SvgIconProps,
    Typography,
    TypographyProps,
} from "@mui/material";
import dayjs from "dayjs";
import { useMenuState } from "hooks/useMenuState";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Language from "@mui/icons-material/Language";
import MyLocationSharp from "@mui/icons-material/MyLocationSharp";
import { useTranslate } from "components/providers/LocalizationProvider";
import { useState } from "react";

type TimezoneOption = {
    icon: React.ComponentType<SvgIconProps>;
    label: string;
    prefixLabel: string;
    offsetInMinutes: number;
};

type TimezoneMenuProps = {
    eventUtcOffsetMinutes: number;
    onChange: (offsetInMinutes: number) => void;
    showPrefix?: boolean;
    textProps?: TypographyProps;
};

export const TimezoneMenu = ({
    onChange,
    eventUtcOffsetMinutes,
    showPrefix = false,
    textProps,
}: TimezoneMenuProps) => {
    const [timezoneOffsetInMinutes, setTimezoneOffsetInMinutes] = useState(
        eventUtcOffsetMinutes,
    );
    const [anchorEl, openMenu, closeMenu] = useMenuState();
    const menuOpen = Boolean(anchorEl);

    const userOffsetInMinutes = dayjs().utcOffset();
    const userTimezoneMatchesEvent =
        userOffsetInMinutes === eventUtcOffsetMinutes;

    const menuOptions: TimezoneOption[] = [
        {
            icon: MyLocationSharp,
            label: useTranslate("Use my current timezone"),
            prefixLabel: useTranslate("In my current timezone"),
            offsetInMinutes: userOffsetInMinutes,
        },
        {
            icon: Language,
            label: useTranslate("Use event's timezone"),
            prefixLabel: useTranslate("In event's timezone"),
            offsetInMinutes: eventUtcOffsetMinutes,
        },
    ];

    const selectedOption = menuOptions.find(
        (option) => option.offsetInMinutes === timezoneOffsetInMinutes,
    );

    const handleSelectTimezone = (offsetInMinutes: number) => {
        setTimezoneOffsetInMinutes(offsetInMinutes);
        onChange(offsetInMinutes);
        closeMenu();
    };

    if (userTimezoneMatchesEvent) return null;

    return (
        <Typography
            sx={{
                display: "inline-flex",
                alignItems: "center",
                verticalAlign: "baseline",
            }}
            variant="inherit"
            {...textProps}
        >
            {showPrefix && <>{selectedOption?.prefixLabel}&nbsp;</>}

            <Link
                component="button"
                onClick={openMenu}
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    verticalAlign: "baseline",
                    borderBottomStyle: "dotted",
                    fontWeight: menuOpen && theme.typography.fontWeightBold,
                    ":hover": {
                        fontWeight: theme.typography.fontWeightBold,
                    },
                    ":active": {
                        fontWeight: theme.typography.fontWeightBold,
                    },
                    "&.Mui-focusVisible": {
                        outline: `3px solid ${theme.palette.input.focus}`,
                        borderRadius: theme.shape.borderRadius,
                    },
                })}
                variant="inherit"
            >
                {`(GMT${dayjs().utcOffset(timezoneOffsetInMinutes).format("Z")})`}

                <KeyboardArrowDownIcon
                    sx={{
                        transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease-in-out",
                    }}
                    fontSize="inherit"
                />
            </Link>

            <Menu anchorEl={anchorEl} open={menuOpen} onClose={closeMenu}>
                {menuOptions.map((option, index) => (
                    <StyledMenuItem
                        key={index}
                        onClick={() =>
                            handleSelectTimezone(option.offsetInMinutes)
                        }
                        selected={
                            option.offsetInMinutes === timezoneOffsetInMinutes
                        }
                    >
                        <option.icon
                            fontSize="small"
                            sx={(theme) => ({
                                marginRight: theme.spacing(1),
                            })}
                        />

                        <Typography variant="body2">
                            {`${option.label} (GMT${dayjs().utcOffset(option.offsetInMinutes).format("Z")})`}
                        </Typography>
                    </StyledMenuItem>
                ))}
            </Menu>
        </Typography>
    );
};

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    "&.Mui-selected": {
        backgroundColor: theme.palette.secondary.lighter,
        "&:hover": {
            backgroundColor: theme.palette.secondary.light,
        },
    },
    "&:hover": {
        backgroundColor: theme.palette.secondary.light,
    },
}));
